'use client';
import { FeaturedHomeWidget } from '@/widgets/Home/FeaturedWidget/Index';
import { Widget } from '@pulsex/types';
import { lazy, Suspense } from 'react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { WidgetErrorBoundary } from './WidgetErrorBoundary';

interface WidgetRenderProps {
    item: Widget;
    data?: any;
    groupedStates?: any;
    isMobile?: boolean;
    dailyPdfData?: any;
    isApple?: boolean;
    nonce?: any;
    menuItems?: any;
}

// Non-home widgets stay out of the homepage client graph (Fancybox / Swiper / details Firebase).
const StyleWidget = lazy(() => import('@/widgets/Style/Index'));
const StaticPage = lazy(() => import('@/components/StaticPage'));
const PopupBanner = lazy(() => import('@/components/PopUp/Index'));

const widgetMap = {
    'home-featured-widget': FeaturedHomeWidget,
    'home-lebanon-widget': dynamic(
        () =>
            import('@/widgets/Home/LebanonWidget/Index').then((mod) => ({
                default: mod.LebanonHomeWidget,
            })),
        { ssr: true, loading: () => <LoadingFallback /> }
    ),
    'home-media-widget': dynamic(
        () =>
            import('@/widgets/Home/MediaWidget/Index').then((mod) => ({
                default: mod.MediaHomeWidget,
            })),
        { ssr: true, loading: () => <LoadingFallback /> }
    ),
    'first-main-column-widget': dynamic(
        () =>
            import('@/widgets/Common/TwoColumnWidget/Index').then((mod) => ({
                default: mod.TwoColumnsWidget,
            })),
        { ssr: true, loading: () => <LoadingFallback /> }
    ),
    'second-main-column-widget': dynamic(
        () =>
            import('@/widgets/Common/TwoColumnWidget/Index').then((mod) => ({
                default: mod.TwoColumnsWidget,
            })),
        { ssr: true, loading: () => <LoadingFallback /> }
    ),
    'home-writers-widget': dynamic(
        () =>
            import('@/widgets/Home/WritersWidget/Index').then((mod) => ({
                default: mod.WritersHomeWidget,
            })),
        { ssr: true, loading: () => <LoadingFallback /> }
    ),
    'sections-featured-widget': lazy(() =>
        import('@/widgets/Sections/Featured/Index').then((mod) => ({
            default: mod.default,
        }))
    ),
    'coverage-featured-widget': lazy(() =>
        import('@/widgets/Sections/CoverageFeatured/Index').then((mod) => ({
            default: mod.default,
        }))
    ),
    'sections-paper-grid-widget': lazy(() =>
        import('@/widgets/Sections/NewsPaperGrid/Index').then((mod) => ({
            default: mod.NewsPaperGridWidget,
        }))
    ),
    'horizontal-banner': lazy(() =>
        import('./Banner/Index').then((mod) => ({ default: mod.HorizontalBanner }))
    ),
    'horizontal-banner-ziad': lazy(() =>
        import('./Banner/BannerZiad').then((mod) => ({ default: mod.BannerZiad }))
    ),
    'gtag-banner': lazy(() =>
        import('./GTagBanner/Index').then((mod) => ({ default: mod.GTagBanner }))
    ),
    'default-details-widget': dynamic(
        () =>
            import('@/widgets/Details/Template/DefaultDetailsTemplate').then((mod) => ({
                default: mod.DefaultDetailsTemplate,
            })),
        { ssr: true, loading: () => <LoadingFallback /> }
    ),
    'details-media-widget': lazy(() =>
        import('@/widgets/Details/Template/MediaDetailsTemplate').then((mod) => ({
            default: mod.MediaDetailsTemplate,
        }))
    ),
    'details-video-widget': lazy(() =>
        import('@/widgets/Details/Template/MediaDetailsTemplate').then((mod) => ({
            default: mod.MediaDetailsTemplate,
        }))
    ),
    'newspaper-details': lazy(() =>
        import('@/widgets/Details/Template/NewspaperDetailsTemplate').then((mod) => ({
            default: mod.NewspaperDetailsTemplate,
        }))
    ),
    'sub-section-widget': lazy(() =>
        import('@/widgets/SubSection/Index').then((mod) => ({
            default: mod.SubSectionWidget,
        }))
    ),
    'authors-widget': lazy(() =>
        import('@/widgets/Authors/Index').then((mod) => ({
            default: mod.AuthorsListWidget,
        }))
    ),
    'posts-slider-widget': lazy(() =>
        import('@/widgets/HeroSlider/Index').then((mod) => ({
            default: mod.HeroSlider,
        }))
    ),
    'author-details-widget': lazy(() =>
        import('@/widgets/Details/Template/AuthorDetailsTemplate').then((mod) => ({
            default: mod.AuthorDetailsTemplate,
        }))
    ),
    'sections-text-cards-list-widget': lazy(() =>
        import('@/widgets/Sections/TextCardsList/Index').then((mod) => ({
            default: mod.NewsPaperRelatedListWidget,
        }))
    ),
    'sections-text-cards-list-pdf-widget': lazy(() =>
        import('@/widgets/Sections/TextCardsList/Index').then((mod) => ({
            default: mod.NewsPaperRelatedListWidget,
        }))
    ),
    'subscribe-info': lazy(() =>
        import('@/widgets/Info/Subscriptions').then((mod) => ({
            default: mod.default,
        }))
    ),
    'jobs-info': lazy(() =>
        import('@/widgets/Info/Employement').then((mod) => ({
            default: mod.default,
        }))
    ),
    'contact-info': lazy(() =>
        import('@/widgets/Info/Contact').then((mod) => ({
            default: mod.default,
        }))
    ),
    'advertise-info': lazy(() =>
        import('@/widgets/Info/Advertise').then((mod) => ({ default: mod.default }))
    ),
    'gallery-images-widget': lazy(() =>
        import('@/widgets/MediaSlider/Index').then((mod) => ({
            default: mod.MediaSlider,
        }))
    ),
    'cards-grid-widget': lazy(() =>
        import('@/widgets/Common/GridWidget/Index').then((mod) => ({
            default: mod.CardsGridWidget,
        }))
    ),
    'cards-grid-col-3-widget': lazy(() =>
        import('@/widgets/Common/GridWidget/Index').then((mod) => ({
            default: mod.CardsGridWidget,
        }))
    ),
    'cards-grid-col-2-widget': lazy(() =>
        import('@/widgets/Common/GridWidget/Index').then((mod) => ({
            default: mod.CardsGridWidget,
        }))
    ),
    'cards-grid-col-4-widget': lazy(() =>
        import('@/widgets/Common/GridWidget/Index').then((mod) => ({
            default: mod.CardsGridWidget,
        }))
    ),
    'cards-grid-col-5-widget': lazy(() =>
        import('@/widgets/Common/GridWidget/Index').then((mod) => ({
            default: mod.CardsGridWidget,
        }))
    ),
    'cards-grid-col-6-widget': lazy(() =>
        import('@/widgets/Common/GridWidget/Index').then((mod) => ({
            default: mod.CardsGridWidget,
        }))
    ),
    'cards-grid-widget-loadmore': lazy(() =>
        import('@/widgets/Common/GridWidget/Index').then((mod) => ({
            default: mod.CardsGridWidget,
        }))
    ),
    'cards-list-widget': lazy(() =>
        import('@/widgets/Common/ListWidget/Index').then((mod) => ({
            default: mod.CardsListWidget,
        }))
    ),
    'cards-slider-widget': lazy(() =>
        import('@/widgets/Common/SliderWidget/Index').then((mod) => ({
            default: mod.CardsSliderWidget,
        }))
    ),
    'newspaper-grid-widget': lazy(() =>
        import('@/widgets/Newspaper/NewspaperGridWidget').then((mod) => ({
            default: mod.NewspaperGridWidget,
        }))
    ),
    'elastic-related-grid-widget': lazy(() =>
        import('@/widgets/Sections/ElasticRelatedSection/Index').then((mod) => ({
            default: mod.ElasticRelatedGrid,
        }))
    ),
    'stats-widget': lazy(() =>
        import('@/widgets/Sections/Stats/Index').then((mod) => ({
            default: mod.StatsWidget,
        }))
    ),
    'ziad-rahbani-bio': lazy(() =>
        import('@/widgets/ZiadRahbani/ZiadBio').then((mod) => ({ default: mod.ZiadBio }))
    ),
    'ziad-rahbani-articles': lazy(() =>
        import('@/widgets/ZiadRahbani/ZiadArticles').then((mod) => ({ default: mod.ZiadArticles }))
    ),
    'ziad-rahbani-articles-about-ziad': lazy(() =>
        import('@/widgets/ZiadRahbani/ZiadArticles').then((mod) => ({ default: mod.ZiadArticles }))
    ),
    'ziad-rahbani-library': lazy(() =>
        import('@/widgets/ZiadRahbani/ZiadArticles').then((mod) => ({ default: mod.ZiadArticles }))
    ),
};

type WidgetStyle = keyof typeof widgetMap;

const LoadingFallback = () => {
    return <div className="rounded-t-3xl h-[583px] w-full bg-neutral-300/30 mt-5 mb-10" />;
};
function findMenuItemByPathname(menus: any, pathname: string) {
    if (!menus || !Array.isArray(menus)) {
        console.warn('findMenuItemByPathname: Invalid menus data provided.');
        return undefined;
    }

    for (const item of menus) {
        if (item.url === pathname) {
            return item;
        }

        if (item.subMenus && Array.isArray(item.subMenus)) {
            for (const subItem of item.subMenus) {
                if (subItem.url === pathname) {
                    return item;
                }
            }
        }
    }

    return undefined;
}

export default function WidgetRender({
    item,
    data,
    groupedStates,
    isMobile,
    dailyPdfData,
    isApple,
    menuItems,
    nonce,
}: WidgetRenderProps) {
    const pathname = usePathname();
    const menuData = findMenuItemByPathname(menuItems, decodeURI(pathname));
    const widgetStyle = item?.style;
    const isAuthorProfilePage = /^\/author\/.+/.test(pathname);

    const { asideMostReadWidget, pdfDetailsContent, urgentNewsAside, groupedPatterns } =
        groupedStates || {};

    if (!widgetStyle) {
        return null;
    }

    if (widgetStyle === 'style-widget') {
        return (
            <WidgetErrorBoundary>
                <Suspense fallback={null}>
                    <StyleWidget data={item} />
                </Suspense>
            </WidgetErrorBoundary>
        );
    }
    if (widgetStyle === 'page-static') {
        return (
            <WidgetErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                    <StaticPage data={item} />
                </Suspense>
            </WidgetErrorBoundary>
        );
    }
    if (widgetStyle === 'timed-banner-widget') {
        return (
            <WidgetErrorBoundary>
                <Suspense fallback={null}>
                    <PopupBanner data={item} />
                </Suspense>
            </WidgetErrorBoundary>
        );
    }

    const ComponentToRender = widgetMap[widgetStyle as WidgetStyle];
    if (!ComponentToRender) {
        // console.warn(`WidgetRender: No component found for style "${widgetStyle}"`);
        return;
    }
    // Each widget is rendered from CMS-driven data whose shape can't be guaranteed at
    // build time. Isolate widget render failures with an ErrorBoundary so a single bad
    // widget degrades gracefully (renders nothing) instead of crashing the whole page
    // for every widget below/above it (see PLATFORM-ALAKHBAR-23, PLATFORM-ALAKHBAR-P).
    return (
        <WidgetErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
                <ComponentToRender
                    asideMostReadWidget={asideMostReadWidget}
                    groupedPatterns={groupedPatterns}
                    pdfDetailsData={pdfDetailsContent}
                    urgentNewsAside={urgentNewsAside}
                    dailyPdf={dailyPdfData}
                    isMobile={isMobile}
                    isApple={isApple}
                    pageData={data}
                    nonce={nonce}
                    data={item}
                    colorTheme={menuData?.icon}
                    headingAs={isAuthorProfilePage ? 'h2' : undefined}
                />
            </Suspense>
        </WidgetErrorBoundary>
    );
}
